
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Eye, Save, RotateCcw, ArrowLeft, FileText, Scale, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { PdfPreview } from "@/components/pdf/PdfPreview";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";

interface PdfSettings {
  company_name: string;
  header_text: string;
  footer_text: string;
  header_image_url: string;
  font_family: string;
  font_size_body: number;
  font_size_footnote: number;
  margin_top: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
  line_spacing: number;
  paragraph_indent: number;
  page_numbering_enabled: boolean;
  page_numbering_position: string;
  lawyer_name: string;
  oab_number: string;
  oab_state: string;
  include_lawyer_signature: boolean;
}

const ESTADOS_BRASIL = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" }
];

const PdfSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<PdfSettings>({
    company_name: "",
    header_text: "",
    footer_text: "",
    header_image_url: "",
    font_family: "Times New Roman",
    font_size_body: 12,
    font_size_footnote: 10,
    margin_top: 30,
    margin_bottom: 20,
    margin_left: 30,
    margin_right: 20,
    line_spacing: 1.5,
    paragraph_indent: 20,
    page_numbering_enabled: true,
    page_numbering_position: "superior-direito",
    lawyer_name: "",
    oab_number: "",
    oab_state: "",
    include_lawyer_signature: false
  });

  const [previewImage, setPreviewImage] = useState<string>("");

  useEffect(() => {
    if (user) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações:', error);
        toast({
          title: "Erro ao carregar configurações",
          description: "Não foi possível carregar suas configurações.",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setSettings({
          company_name: data.company_name || "",
          header_text: data.header_text || "",
          footer_text: data.footer_text || "",
          header_image_url: data.header_image_url || "",
          font_family: data.font_family || "Times New Roman",
          font_size_body: data.font_size_body || 12,
          font_size_footnote: data.font_size_footnote || 10,
          margin_top: data.margin_top || 30,
          margin_bottom: data.margin_bottom || 20,
          margin_left: data.margin_left || 30,
          margin_right: data.margin_right || 20,
          line_spacing: data.line_spacing || 1.5,
          paragraph_indent: data.paragraph_indent || 20,
          page_numbering_enabled: data.page_numbering_enabled ?? true,
          page_numbering_position: data.page_numbering_position || "superior-direito",
          lawyer_name: data.lawyer_name || "",
          oab_number: data.oab_number || "",
          oab_state: data.oab_state || "",
          include_lawyer_signature: data.include_lawyer_signature || false
        });
        setPreviewImage(data.header_image_url || "");
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione apenas arquivos de imagem.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "A imagem deve ter no máximo 2MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewImage(result);
        setSettings(prev => ({
          ...prev,
          header_image_url: result
        }));
      };
      reader.readAsDataURL(file);

      toast({
        title: "Imagem carregada",
        description: "Imagem carregada com sucesso. Clique em 'Salvar' para aplicar as alterações.",
      });
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      toast({
        title: "Erro ao processar imagem",
        description: "Não foi possível processar a imagem selecionada.",
        variant: "destructive",
      });
    }
  };

  const applyABNTDefaults = () => {
    setSettings(prev => ({
      ...prev,
      font_family: "Times New Roman",
      font_size_body: 12,
      font_size_footnote: 10,
      margin_top: 30,
      margin_bottom: 20,
      margin_left: 30,
      margin_right: 20,
      line_spacing: 1.5,
      paragraph_indent: 20,
      page_numbering_enabled: true,
      page_numbering_position: "superior-direito"
    }));
    
    toast({
      title: "Padrão ABNT aplicado",
      description: "Configurações de formatação jurídica ABNT NBR 14724 aplicadas com sucesso!",
    });
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('pdf_settings')
        .upsert({
          user_id: user.id,
          company_name: settings.company_name,
          header_text: settings.header_text,
          footer_text: settings.footer_text,
          header_image_url: settings.header_image_url,
          font_family: settings.font_family,
          font_size_body: settings.font_size_body,
          font_size_footnote: settings.font_size_footnote,
          margin_top: settings.margin_top,
          margin_bottom: settings.margin_bottom,
          margin_left: settings.margin_left,
          margin_right: settings.margin_right,
          line_spacing: settings.line_spacing,
          paragraph_indent: settings.paragraph_indent,
          page_numbering_enabled: settings.page_numbering_enabled,
          page_numbering_position: settings.page_numbering_position,
          lawyer_name: settings.lawyer_name,
          oab_number: settings.oab_number,
          oab_state: settings.oab_state,
          include_lawyer_signature: settings.include_lawyer_signature,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Erro ao salvar:', error);
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar as configurações.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Configurações salvas",
        description: "Suas configurações de papel timbrado foram salvas com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      company_name: "",
      header_text: "",
      footer_text: "",
      header_image_url: "",
      font_family: "Times New Roman",
      font_size_body: 12,
      font_size_footnote: 10,
      margin_top: 30,
      margin_bottom: 20,
      margin_left: 30,
      margin_right: 20,
      line_spacing: 1.5,
      paragraph_indent: 20,
      page_numbering_enabled: true,
      page_numbering_position: "superior-direito",
      lawyer_name: "",
      oab_number: "",
      oab_state: "",
      include_lawyer_signature: false
    });
    setPreviewImage("");
    toast({
      title: "Configurações resetadas",
      description: "Todas as configurações foram resetadas.",
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Carregando configurações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidebarInset className="flex-1">
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <h1 className="font-semibold">Papel Timbrado</h1>
          </div>
        </header>
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 bg-background overflow-auto">
            <div className="container mx-auto py-6 px-4 max-w-7xl">
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Papel Timbrado</h1>
            <p className="text-muted-foreground">
              Personalize o cabeçalho, formatação jurídica e rodapé dos seus PDFs
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configurações */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Logo do Cabeçalho
              </CardTitle>
              <CardDescription>
                Faça upload do logo da sua empresa para aparecer no cabeçalho dos PDFs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo-upload">Selecionar Imagem</Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  data-tour="logo-upload"
                />
                <p className="text-xs text-muted-foreground">
                  Formatos aceitos: PNG, JPG, JPEG. Tamanho máximo: 2MB
                </p>
              </div>
              
              {previewImage && (
                <div className="space-y-2">
                  <Label>Preview da Imagem</Label>
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <img 
                      src={previewImage} 
                      alt="Preview do logo" 
                      className="max-h-20 w-auto mx-auto"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Informações Profissionais</CardTitle>
              <CardDescription>
                Configure as informações que aparecerão no cabeçalho
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-tour="company-info">
              <div className="space-y-2">
                <Label htmlFor="company-name">Título do cabeçalho</Label>
                <Input
                  id="company-name"
                  value={settings.company_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Ex: Escritório Jurídico Silva & Associados"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="header-text">Texto do Cabeçalho</Label>
                <Textarea
                  id="header-text"
                  value={settings.header_text}
                  onChange={(e) => setSettings(prev => ({ ...prev, header_text: e.target.value }))}
                  placeholder="Ex: Advogados Especialistas em Direito Civil | OAB/SP 123.456"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Rodapé</CardTitle>
              <CardDescription>
                Configure as informações que aparecerão no rodapé dos PDFs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="footer-text">Texto do Rodapé</Label>
                <Textarea
                  id="footer-text"
                  value={settings.footer_text}
                  onChange={(e) => setSettings(prev => ({ ...prev, footer_text: e.target.value }))}
                  placeholder="Ex: Rua das Flores, 123 - Centro - São Paulo/SP | Tel: (11) 1234-5678 | contato@escritorio.com.br"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3" data-tour="save-settings">
            <Button onClick={handleSave} disabled={isSaving} className="flex-1">
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Salvando..." : "Salvar Configurações"}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Resetar
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview do PDF
              </CardTitle>
              <CardDescription>
                Veja como ficará seu PDF com as configurações atuais
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PdfPreview settings={settings} />
            </CardContent>
          </Card>
        </div>
      </div>
            </div>
          </div>
        </main>
      </SidebarInset>
  );
};

export default PdfSettings;
